import { useState } from 'react'
import { Button } from '@2/components/ui/button'
import { Column, Row, Table } from '@tanstack/react-table'
import * as cuid from 'cuid'
import * as React from 'react'
import { FormatDate } from '@/Utils/Localisation/DateFormatter'
import download from 'downloadjs'
import Papa from 'papaparse'
import Formatter from '@/Components/Formatter'
import { trpc } from '@/system/trpc'
import SessionStore from '@/State/SessionStore'
import { useNavigate } from '@tanstack/react-router'
import { ExportColumn, Fraction, OrganisationReport } from '@/version2/types'
import { DataTableActions } from '@/version2/components/data-table/advanced/data-table-action'
import {
    updateCacheItem,
    deleteCacheItem,
    addCacheItem,
} from '@/version2/cache'
import { setPageTitle } from '@/version2/layout/layout-store'
import {
    useOrganisationReport,
    setOrganisationReport,
} from './invoice-report-store'
import { useSaveToast } from '@2/components/hooks/save-toast'

interface InvoiceReportActionsProps<TData>
    extends React.HTMLAttributes<HTMLDivElement> {
    table: Table<TData>
    exportColumns?: ExportColumn[]
}

export function InvoiceReportActions<TData>({
    table,
    exportColumns = [],
}: InvoiceReportActionsProps<TData>) {
    const navigate = useNavigate()
    const [dialogContent, setDialogContent] = useState<string | null>(null)
    const [nameReport, setNameReport] = useState<string | null>(null)

    const organisationReport = useOrganisationReport()
    const checkDisableButton =
        !organisationReport?.id &&
        !SessionStore.organisation?.defaultInvoiceReportId

    const handleOpenDialog = (content: string) => {
        setNameReport(null)
        setDialogContent(content)
    }

    const showSaveAsToast = useSaveToast({
        saveId: 'invoice-report-save-as',
        onSave: async () => {
            const newId = cuid()
            const result = await trpc.invoiceReport.saveReport.mutate({
                id: newId,
                name: nameReport,
                organisationId: organisationReport.organisationId,
                filters: organisationReport.filters,
                type: 'invoice',
                columns: organisationReport.columns,
                options: organisationReport.options,
            })
            addCacheItem('organisationReports', {
                id: newId,
                name: nameReport,
                type: 'invoice',
            })
            setNameReport(null)
            setDialogContent(null)
            setOrganisationReport({
                ...organisationReport,
                ...result,
            })
            navigate({
                search: {
                    report: newId,
                },
            })
        },
        loadingMessage: 'Saving new report...',
        successMessage: 'New report saved successfully',
        errorMessage: 'Failed to save new report',
    })

    // Save existing report
    const showSaveToast = useSaveToast({
        saveId: 'invoice-report-save',
        onSave: async () => {
            const result = await trpc.invoiceReport.saveReport.mutate({
                id: organisationReport.id,
                organisationId: organisationReport.organisationId,
                filters: organisationReport.filters,
                columns: organisationReport.columns,
                options: organisationReport.options,
            })
            setDialogContent(null)
            setOrganisationReport({
                ...organisationReport,
                ...result,
            })
            return result
        },
        loadingMessage: 'Saving report...',
        successMessage: 'Report saved successfully',
        errorMessage: 'Failed to save report',
    })

    // Rename operation
    const showRenameToast = useSaveToast({
        saveId: 'invoice-report-rename',
        onSave: async () => {
            if (!nameReport || nameReport === organisationReport.name) return
            const result = await trpc.invoiceReport.saveReport.mutate({
                id: organisationReport.id,
                organisationId: organisationReport.organisationId,
                name: nameReport,
            })
            updateCacheItem('organisationReports', organisationReport.id, {
                name: nameReport,
                type: 'invoice',
            })
            setPageTitle(nameReport)
            setNameReport(null)
            setDialogContent(null)
            setOrganisationReport({
                ...organisationReport,
                ...result,
            })
            return result
        },
        loadingMessage: 'Renaming report...',
        successMessage: 'Report renamed successfully',
        errorMessage: 'Failed to rename report',
    })

    // Delete operation
    const showDeleteToast = useSaveToast({
        saveId: 'invoice-report-delete',
        onSave: async () => {
            const result = await trpc.invoiceReport.saveReport.mutate({
                id: organisationReport.id,
                organisationId: organisationReport.organisationId,
                deletedAt: new Date(),
            })
            deleteCacheItem('organisationReports', organisationReport.id)
            setDialogContent(null)
            navigate({ search: {} })
            return result
        },
        loadingMessage: 'Deleting report...',
        successMessage: 'Report deleted successfully',
        errorMessage: 'Failed to delete report',
    })

    // Set Default operation
    const showSaveDefaultToast = useSaveToast({
        saveId: 'invoice-report-set-default',
        onSave: async () => {
            return await trpc.invoiceReport.setDefaultReport.mutate({
                id: organisationReport.id,
            })
        },
        loadingMessage: 'Setting as default...',
        successMessage: 'Set as default report',
        errorMessage: 'Failed to set as default',
    })

    const handleSetDefault = () => {
        showSaveDefaultToast()
    }

    const handleDialogSubmit = (dialogContent: string) => {
        setDialogContent(null)
        switch (dialogContent) {
            case 'Save':
                showSaveToast()
                break
            case 'Save as':
                showSaveAsToast()
                break
            case 'Rename Report':
                showRenameToast()
                break
            case 'Delete Report':
                showDeleteToast()
                break
        }
    }

    const csvFormatter = (
        data: Row<TData>,
        column: Column<TData, unknown>
    ): string => {
        const value = data.getValue(column?.id)
        switch (column.columnDef.meta?.type) {
            case 'progress':
            case 'progressBar':
                const { numerator, denominator } = value as Fraction
                if (numerator || denominator) {
                    return `${Formatter.progress(
                        numerator || 0
                    )} / ${Formatter.progress(denominator || 0)}`
                } else {
                    return ''
                }
            case 'date':
                return data
                    ? FormatDate(new Date(value as string), {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                      })
                    : ''
            default:
                return value as string
        }
    }

    const handleExportToCSV = () => {
        const results: Array<string>[] = []
        const headers = table
            .getAllColumns()
            .filter((item) => organisationReport?.columns?.includes(item.id))
        const firstRow: Array<string> = []
        headers.forEach((header) => firstRow.push(header.columnDef.meta?.title))
        results.push(firstRow)
        // Add data
        table.getRowModel().rows.forEach((item: Row<TData>) => {
            const row: string[] = []
            headers.forEach((header) => {
                row.push(csvFormatter(item, header))
            })
            results.push(row)
        })

        download(Papa.unparse(results), `invoice.csv`, 'text/csv')
    }
    return (
        <>
            <DataTableActions
                handleExportToCSV={handleExportToCSV}
                handleDialogSubmit={handleDialogSubmit}
                handleOpenDialog={handleOpenDialog}
                handleSetDefault={handleSetDefault}
                dialogContent={dialogContent}
                setDialogContent={setDialogContent}
                setNameReport={setNameReport}
                nameReport={nameReport}
                organisationReport={organisationReport}
                checkDisableButton={checkDisableButton}
                exportColumns={exportColumns}
            />
        </>
    )
}
